/**
 * /app/{tenantSlug}/loans/manage — Loans & advances, the HR side (Run →
 * Money; QA 2026-10 offboarding #6).
 *
 * Before this page HR could not record a loan or salary advance for an
 * employee at all — POST /loans always booked it for the caller — so an
 * advance the company had agreed with someone (and the F&F's recovery of
 * it at exit) was impossible, and every loan screen lived under the "Me"
 * surface. Here HR sees every employee's loans and records one on their
 * behalf (POST /loans/on-behalf): it lands as a request like a
 * self-request, the employee is notified, and the usual finance approve →
 * disburse steps (with their 2FA step-up) follow on the loan's page.
 *
 * Finance sees the list (company admin, HR manager, accountant — the
 * backend's isLoanFinance returns every employee's loans to them; /loans
 * redirects them here). Recording a loan for someone is HR-admin only
 * (the server's hrRole gate on POST /loans/on-behalf is the boundary), so
 * an accountant gets the list, the filter and Approvals, without Record.
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { HandCoins, Inbox, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isAccountingAdmin, isHRAdmin } from "@/lib/nav";
import { toApiError } from "@/lib/api";
import { useCreateLoanOnBehalf, useLoans } from "@/hooks/use-loans";
import type { LoanKind, LoanStatus } from "@/lib/api/loans";
import { rupeesToPaise } from "@/lib/expense-format";
import { sentence } from "@/lib/offboarding-rules";
import { PageHeader } from "@/components/shared/page-header";
import { EmployeeDirectoryPicker } from "@/components/shared/employee-directory-picker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LoanTable } from "./app.$tenantSlug.loans.index";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/loans/manage")({
  component: ManageLoansRoute,
});

const ALL = "__all__";
const STATUS_FILTERS: { value: LoanStatus; label: string }[] = [
  { value: "requested", label: "Requested" },
  { value: "approved", label: "Approved" },
  { value: "disbursed", label: "Disbursed (Recovering)" },
  { value: "closed", label: "Closed" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
  { value: "written_off", label: "Written Off" },
];

function ManageLoansRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const hr = isHRAdmin(user?.role);
  const finance = isAccountingAdmin(user?.role);
  const [status, setStatus] = useState<string>(ALL);
  const { data: loans, isLoading, isError, error } = useLoans(
    finance ? { status: status === ALL ? undefined : (status as LoanStatus) } : undefined,
  );
  const { data: requested } = useLoans(
    finance ? { status: "requested", excludeSelf: true } : undefined,
  );
  const pendingCount = requested?.length ?? 0;

  if (!finance) {
    return (
      <>
        <PageHeader title="Loans & Advances" />
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Company admins, HR managers and accountants see employees&apos;
            loans and advances here. Your own loans are under{" "}
            <Link
              to="/app/$tenantSlug/me/loans"
              params={{ tenantSlug }}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Me → Loans
            </Link>
            .
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Loans & Advances"
        description={
          hr
            ? "Every employee's interest-free loans and salary advances. Record one agreed with an employee; it then goes through the usual approve and disburse steps, and is recovered through payroll (or the F&F at exit)."
            : "Every employee's interest-free loans and salary advances, recovered through payroll (or the F&F at exit). Open a loan to approve or disburse it."
        }
        actions={
          <>
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/approvals" params={{ tenantSlug }} search={{ tab: "loans" }}>
                <Inbox aria-hidden className="h-4 w-4" />
                Approvals
                {pendingCount > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {pendingCount}
                  </Badge>
                )}
              </Link>
            </Button>
            {hr && <RecordLoanDialog selfId={user?.user_id} />}
          </>
        }
      />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor="loans-status-filter" className="text-sm">
            Status
          </Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="loans-status-filter" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Statuses</SelectItem>
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isError ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-destructive">
              Could not load loans: {toApiError(error).message}
            </CardContent>
          </Card>
        ) : (
          <LoanTable loans={loans} isLoading={isLoading} tenantSlug={tenantSlug} showEmployee from="manage" />
        )}
      </div>
    </>
  );
}

/** HR records a loan / advance for an employee (POST /loans/on-behalf). */
function RecordLoanDialog({ selfId }: Readonly<{ selfId: number | undefined }>) {
  const create = useCreateLoanOnBehalf();
  const [open, setOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState<number | undefined>();
  const [kind, setKind] = useState<LoanKind>("advance");
  const [principalStr, setPrincipalStr] = useState("");
  const [tenure, setTenure] = useState("6");
  const [reason, setReason] = useState("");

  const principalPaise = rupeesToPaise(principalStr);
  const tenureMonths = kind === "advance" ? 1 : Number(tenure) || 0;
  const valid = useMemo(
    () =>
      !!employeeId &&
      principalPaise != null &&
      principalPaise > 0 &&
      tenureMonths >= 1 &&
      tenureMonths <= 120,
    [employeeId, principalPaise, tenureMonths],
  );

  function reset() {
    setEmployeeId(undefined);
    setKind("advance");
    setPrincipalStr("");
    setTenure("6");
    setReason("");
  }

  async function submit() {
    if (!valid || !employeeId || principalPaise == null) return;
    try {
      await create.mutateAsync({
        employee_id: employeeId,
        kind,
        principal_paise: principalPaise,
        tenure_months: tenureMonths,
        reason: reason.trim() || undefined,
      });
      toast.success(
        `${kind === "advance" ? "Advance" : "Loan"} recorded — it now needs a finance decision before it is paid.`,
      );
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(sentence(toApiError(err).message));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button className="gap-1.5" data-testid="loans-record-on-behalf">
          <Plus aria-hidden className="h-4 w-4" />
          Record for an Employee
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandCoins aria-hidden className="h-4 w-4" />
            Record a Loan or Advance
          </DialogTitle>
          <DialogDescription>
            For an advance or loan already agreed with the employee. It is
            recorded as a request in their name (they are told), then a
            finance user approves and disburses it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Employee</Label>
            <EmployeeDirectoryPicker
              value={employeeId}
              onChange={setEmployeeId}
              excludeId={selfId}
              placeholder="Choose the employee"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={kind === "advance" ? "default" : "outline"}
                onClick={() => setKind("advance")}
              >
                Advance (Next Payroll)
              </Button>
              <Button
                type="button"
                size="sm"
                variant={kind === "loan" ? "default" : "outline"}
                onClick={() => setKind("loan")}
              >
                Loan (EMIs)
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="loan-on-behalf-principal">Amount (₹)</Label>
            <Input
              id="loan-on-behalf-principal"
              inputMode="decimal"
              placeholder="25000.00"
              value={principalStr}
              onChange={(e) => setPrincipalStr(e.target.value)}
            />
          </div>
          {kind === "loan" && (
            <div className="space-y-1.5">
              <Label htmlFor="loan-on-behalf-tenure">Tenure (Months)</Label>
              <Input
                id="loan-on-behalf-tenure"
                type="number"
                min={1}
                max={120}
                value={tenure}
                onChange={(e) => setTenure(e.target.value)}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="loan-on-behalf-reason">Reason (Optional)</Label>
            <Textarea
              id="loan-on-behalf-reason"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Festival advance, medical emergency, relocation…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!valid || create.isPending} className="gap-1.5">
            {create.isPending && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
            Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
